import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useCreateTestTemplate, useUpdateTestTemplate } from "../../hooks"
import type { TestTemplateFormValues } from "../../types"
import { testTemplateFormSchema } from "../../types"
import { serializeTestTemplateForm } from "../../utils"

import TestTemplateForm from "./TestTemplateForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { TestTemplate } from "@/graphql/codegen/graphql"

interface TestTemplateFormDialogProps {
  testTemplate?: TestTemplate | null
  onSubmit: () => void
  onClose: () => void
}

const TestTemplateFormDialog: FC<TestTemplateFormDialogProps> = ({ testTemplate, onSubmit, onClose }) => {
  const isCreateMode = !testTemplate
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<TestTemplateFormValues>({
    defaultValues: {
      description: testTemplate?.description ?? "",
      resultsUnitOfMeasure: testTemplate?.resultsUnitOfMeasure ?? "",
      comparison: testTemplate?.comparison ?? "",
    },
    resolver: zodResolver(testTemplateFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
  } = methods
  const canSubmit = isDirty && isValid && !isSubmitting
  const { create } = useCreateTestTemplate(onSubmit)
  const { update } = useUpdateTestTemplate(onSubmit)

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const submitForm = async (formValues: TestTemplateFormValues) => {
    const payload = serializeTestTemplateForm(formValues)

    if (isCreateMode) {
      await create(payload)
    } else {
      await update({ ...payload, testTemplateId: testTemplate.testTemplateId })
    }
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={onClose} />
  ) : (
    <Dialog open fullWidth onClose={handleClose} data-testid='test-template-form-dialog'>
      <DialogHeader title={`${isCreateMode ? "Add New" : "Edit"} Test Template`} onClose={handleClose} />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <TestTemplateForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button data-testid='submit-button' variant='primary' disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          {isCreateMode ? "Save" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TestTemplateFormDialog
